import moment from "moment";
import { useState } from "react";
import Swal from "sweetalert2";
import {
  useKycsQuery,
  useTraiterKycMutation,
} from "../../../../utils/api/kyc/kyc.api";
import { Kyc, KYC_STATUT, TYPES_PIECE } from "../../../../utils/api/kyc/kyc.type";
import { ApiUrl, COLORS } from "../../../../utils/constants";
import SideBarAdmin from "../Layouts/SideBarAdmin/SideBarAdmin";
import TopBar from "../Layouts/TopBar/TopBar";

// Les fichiers sont servis sous MEDIA_URL par le backend.
const lien = (chemin: string | null) =>
  !chemin ? null : chemin.startsWith("http") ? chemin : `${ApiUrl}${chemin}`;

const libellePiece = (v: string) =>
  TYPES_PIECE.find((t) => t.value === v)?.label || v;

function Vignette({ titre, chemin }: { titre: string; chemin: string | null }) {
  const url = lien(chemin);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="me-2 mb-2 d-inline-block">
      <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>{titre}</div>
      <img
        src={url}
        alt={titre}
        style={{
          width: 150,
          height: 100,
          objectFit: "cover",
          borderRadius: 6,
          border: "1px solid #c4c4c4",
        }}
      />
    </a>
  );
}

function KycAdmin() {
  const [statut, setStatut] = useState("en_attente");
  const [recherche, setRecherche] = useState("");
  // Le filtre part au serveur : la liste peut devenir longue, on ne la
  // rapatrie pas entierement pour filtrer dans le navigateur.
  const { data: dossiers = [], isLoading } = useKycsQuery({
    statut: statut || undefined,
    q: recherche.trim() || undefined,
  });
  const [traiter, { isLoading: traitement }] = useTraiterKycMutation();

  const decider = async (k: Kyc, decision: "valide" | "refuse") => {
    let motif: string | undefined;
    if (decision === "valide") {
      // Valider ouvre le droit de retirer de l'argent : on demande
      // confirmation plutot que de l'accorder sur un seul clic.
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Valider cette identité ?",
        html: `<b>${k.user_nom || ""}</b><br/>${libellePiece(k.type_piece)} n° ${
          k.numero_piece
        }<br/><br/>Cette personne pourra demander des retraits.`,
        showCancelButton: true,
        cancelButtonText: "Annuler",
        confirmButtonText: "Valider",
        confirmButtonColor: COLORS.green,
      });
      if (!isConfirmed) return;
    }
    if (decision === "refuse") {
      const { value, isDismissed } = await Swal.fire({
        title: "Refuser la vérification",
        input: "textarea",
        inputLabel: "Motif (communiqué à la personne)",
        inputPlaceholder: "Pièce illisible, visage non visible…",
        showCancelButton: true,
        cancelButtonText: "Annuler",
        confirmButtonText: "Refuser",
        confirmButtonColor: COLORS.red,
      });
      if (isDismissed || !value) return;
      motif = value;
    }
    const res: any = await traiter({ slug: k.slug, statut: decision, motif });
    if (res?.error) {
      Swal.fire({
        icon: "error",
        title: "Action impossible",
        text: res.error?.data?.message || "Veuillez réessayer.",
        confirmButtonColor: COLORS.green,
      });
      return;
    }
    Swal.fire({
      icon: "success",
      title: decision === "valide" ? "Identité validée" : "Vérification refusée",
      text:
        decision === "valide"
          ? "La personne peut désormais demander des retraits."
          : "La personne a été notifiée du motif.",
      iconColor: COLORS.green,
      showConfirmButton: false,
      timer: 2000,
    });
  };

  return (
    <div className="parametre-page">
      <TopBar index={3} />
      <SideBarAdmin index={3} />
      <div className="parametre-component m-tableau-bord pt-3 flex-r">
        <div className="admin-page-container">
          <div className="p4 p-xxl-3">
            <div className="row">
              <div className="col-md-12 maajjal-table-card px-3 py-4 ml-2 mr-3">
                <h3 className="tb-mes-projet-titre mb-4">
                  Vérifications d'identité
                </h3>

                <div className="mb-3 d-flex flex-wrap align-items-center gap-3">
                  <div>
                    <label className="me-2" htmlFor="filtre-kyc">
                      Statut
                    </label>
                    <select
                      id="filtre-kyc"
                      className="form-select d-inline-block"
                      style={{ width: 220 }}
                      value={statut}
                      onChange={(e) => setStatut(e.target.value)}
                    >
                      <option value="">Tous</option>
                      {Object.entries(KYC_STATUT).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex-grow-1" style={{ maxWidth: 380 }}>
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Rechercher un nom, un email, un n° de pièce"
                      value={recherche}
                      onChange={(e) => setRecherche(e.target.value)}
                    />
                  </div>
                  {(recherche || statut) && (
                    <button
                      type="button"
                      className="btn tb-action-btn"
                      onClick={() => {
                        setRecherche("");
                        setStatut("");
                      }}
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>

                {isLoading && <p>Chargement…</p>}
                {!isLoading && dossiers.length === 0 && (
                  <p>
                    {recherche
                      ? `Aucun dossier ne correspond à « ${recherche} ».`
                      : "Aucun dossier pour ce filtre."}
                  </p>
                )}

                {dossiers.map((k) => (
                  <div
                    key={k.slug}
                    className="p-3 mb-3"
                    style={{
                      background: "#fff",
                      borderLeft: `4px solid ${KYC_STATUT[k.statut].color}`,
                      boxShadow: "0px 2px 10px rgba(0,0,0,0.06)",
                    }}
                  >
                    <div className="d-flex justify-content-between flex-wrap">
                      <div>
                        <div style={{ fontWeight: 600 }}>{k.user_nom}</div>
                        <div style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
                          {k.user_email}
                        </div>
                        <div style={{ fontSize: 12, marginTop: 4 }}>
                          {libellePiece(k.type_piece)} — n° {k.numero_piece}
                        </div>
                        <div style={{ fontSize: 11, color: "rgba(0,0,0,0.5)" }}>
                          Soumis le{" "}
                          {moment(k.date_soumission).format("DD/MM/YYYY à HH:mm")}
                        </div>
                      </div>
                      <div
                        style={{
                          color: KYC_STATUT[k.statut].color,
                          fontWeight: 600,
                        }}
                      >
                        {KYC_STATUT[k.statut].label}
                      </div>
                    </div>

                    <div className="mt-3">
                      <Vignette titre="Recto" chemin={k.recto} />
                      <Vignette titre="Verso" chemin={k.verso} />
                      <Vignette titre="Photo avec la pièce" chemin={k.selfie} />
                    </div>

                    {k.motif && (
                      <div className="mt-2" style={{ fontSize: 13 }}>
                        Motif : {k.motif}
                      </div>
                    )}

                    {k.statut === "en_attente" && (
                      <div className="mt-3 d-flex gap-2">
                        <button
                          type="button"
                          disabled={traitement}
                          className="btn tb-action-btn tb-action-btn-retirer"
                          onClick={() => decider(k, "valide")}
                        >
                          Valider
                        </button>
                        <button
                          type="button"
                          disabled={traitement}
                          className="btn tb-action-btn"
                          style={{ borderColor: COLORS.red, color: COLORS.red }}
                          onClick={() => decider(k, "refuse")}
                        >
                          Refuser
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default KycAdmin;
